import { reactive } from 'vue';
import { useConfirm, useNotif } from '@orion.ui/orion';
import { readableUserStatus, userIsCreator } from '@/utils/tools';
import { USER_STATUS } from '@/typings/enums/USER_STATUS';
import BaseEntity from '@/entity/BaseEntity';
import useOrganizationUserService from '@/services/organization-user/OrganizationUserService';
import useMembershipService from '@/services/MembershipService';
import { SUITE_URL } from '@/utils/CONST';

type BaseOrganizationUser = Common<[IOrganizationUserDetailModel, IOrganizationUserListItemModel]>

export default abstract class BaseOrganizationUserEntity<T extends BaseOrganizationUser> extends BaseEntity<T> {
	private readonly privateState = reactive({
		isSendingMail: false,
		isDeleting: false,
	});

	get isSendingMail () { return this.privateState.isSendingMail; }
	get isDeleting () { return this.privateState.isDeleting; }

	get authLogin () { return this.state.user?.authLogin; }
	get avatarId () { return this.state.user?.avatarId; }
	get firstName () { return this.state.user?.firstName; }
	get lastName () { return this.state.user?.lastName; }
	get name () { return this.state.user?.name; }
	get status () { return this.state.user?.status; }
	get userId () { return this.state.user?.id; }

	get isSelf () { return this.userId === this.self.id; }
	get isCreator () { return !!this.state.user && userIsCreator(this.state.user); }
	get isActivated () { return this.status === USER_STATUS.Activated; }
	get isInactivated () { return this.status === USER_STATUS.Inactivated; }
	get readableStatus () { return readableUserStatus(this.status); }
	get suiteUserId () { return this.state.user?.suiteUserId; }
	get hasAccessToArmado () { return this.state.user?.hasAccessToArmado; }
	get isEnabledForSuite () { return this.state.user?.isEnabledForSuite; }

	get isHidden () {
		if (!this.self.suiteUserId) return false;
		return !!this.self.suiteUserId && (!this.hasAccessToArmado || !this.isEnabledForSuite);
	}

	get isHiddenTooltip () {
		if (!this.hasAccessToArmado && this.isEnabledForSuite) return 'Cet utilisateur n\'a pas accès à Armado';
		if (!this.isEnabledForSuite && this.hasAccessToArmado) return 'Cet utilisateur n\'est pas activé sur la SUITE';
		if (!this.isEnabledForSuite && !this.hasAccessToArmado) return 'Cet utilisateur n\'est pas activé sur la SUITE et n\'a pas accès à Armado';
		return undefined;
	}

	get roles () { return this.state.user?.roleUsers; }
	set roles (val) {
		this.state.user = {
			...(this.state.user ?? {}),
			roleUsers: val ?? [],
		} as IUserForOrganizationUserListItemModel;
	}

	get tags () { return this.state.user?.tagUsers; }
	set tags (val) {
		this.state.user = {
			...(this.state.user ?? {}),
			tagUsers: val ?? [],
		} as IUserForOrganizationUserListItemModel;
	}

	get avatarUrl () {
		return this.suiteUserId
			? `${SUITE_URL}/app/user/${this.suiteUserId}/avatar`
			: this.avatarId;
	}


	async sendActivationMailAsync () {
		if (!this.userId) return;

		try {
			this.privateState.isSendingMail = true;
			await useOrganizationUserService().sendActivationMailAsync(this.userId);
		} finally {
			this.privateState.isSendingMail = false;
		}
	}

	async deleteAsync () {
		try {
			if (await useConfirm(`Confirmez-vous la suppression de cet utilisateur ?`)) {
				this.privateState.isDeleting = true;
				await useOrganizationUserService().deleteAsync(this.id);
			}
		} finally {
			this.privateState.isDeleting = false;
		}
	}

	async deleteMembershipAsync (membershipId: number) {
		if (!this.self.organizationGuard()) return;

		if (this.isSelf) {
			useNotif.warning({
				title: `Dissociation impossible`,
				message: `Vous ne pouvez pas vous-même vous dissocier d'une société.`,
				duration: 0,
			});
			return;
		}

		if (this.isCreator) {
			useNotif.warning({
				title: `Dissociation impossible`,
				message: `Vous ne pouvez pas dissocier le "Créateur" d'une société.`,
				duration: 0,
			});
			return;
		}

		await useMembershipService().deleteAsync(membershipId);
	}

	addUserRole (role: IRoleUserDetailModel) {
		this.state.user?.roleUsers.push(role);
	}

	deleteUserRole (roleUserId: number) {
		this.state.user?.roleUsers.deleteWhere(x => x.id === roleUserId);
	}
}
